/**
 * =============================================================================
 * derive(log, drafts) -> state
 * =============================================================================
 *
 * The whole state machine of the panel. Two stores in, one object out, and
 * every card's status comes from here.
 *
 *   const [log, dispatch] = useReducer(logReducer, { entries: [] });
 *   const [drafts, setDrafts] = useState({});
 *   const state = useMemo(() => derive(log, drafts), [log, drafts]);
 *
 * PURE. Same inputs, same output, and it never mutates the entries it walks —
 * otherwise a re-render corrupts the log it just read.
 *
 * =============================================================================
 * TWO RULES THAT LOOK LIKE STYLE AND ARE NOT
 * =============================================================================
 *
 * 1. `problem` IS PASSED BY REFERENCE, NEVER CLONED.
 *
 *    `drafts` is in the useMemo dependency array, so this function re-runs on
 *    every keystroke. Cloning would hand out a brand-new `problem` object each
 *    time, and anything comparing it — React.memo, an effect's dependency
 *    array — would see a change that did not happen. An effect that fires a
 *    request off `problem` would fire on every character typed.
 *
 *    Passing the reference means `problem` becomes a new object only when a new
 *    log entry arrives, which is a real event. The log is protected by this
 *    function being pure and by nothing writing to what it returns, NOT by
 *    defensive copying.
 *
 * 2. DRAFTS SET STATUS AND NOTHING ELSE.
 *
 *    A draft is a full copy of the problem, made when `edit` is pressed, so it
 *    already holds everything the card needs. This function reads `drafts` only
 *    to set `status: 'editing'` and `answer: null`. It never takes CONTENT from
 *    a draft.
 *
 *    State must know a label is being edited — otherwise a card cannot tell
 *    `editing` from `answered` and the stale answer keeps showing while the
 *    student types. But state must not carry the draft's content, or `problem`
 *    changes on every keystroke and rule 1 is defeated from the other side.
 *
 *    So: an editing card reads `drafts[label]` for its content and `state` for
 *    its status. Two reads, deliberately.
 * =============================================================================
 */

/**
 * @param {{entries: Array}} log     append-only, entries in any order
 * @param {Object}           drafts  keyed by label, full problem copies
 * @returns {{submissionId: number, problems: Object}}
 */
export function derive(log, drafts) {
  const entries = (log && log.entries) || [];
  const problems = {};

  // ── 1. WALK THE LOG, OLDEST TO NEWEST ─────────────────────────────────────
  //
  // Sorted ascending by submissionId and applied in order, overwriting as it
  // goes. The last entry to mention a label wins BY VIRTUE OF BEING WRITTEN
  // LAST — there is no "find the newest" search anywhere.
  //
  // That is what makes out-of-order arrival need no rule: the original can land
  // after resubmit-2 and the output is identical, because the sort happens
  // here rather than at arrival time.
  //
  // Copied before sorting. Array.prototype.sort mutates in place, and the log
  // belongs to the reducer.
  const ordered = entries.slice().sort((a, b) => a.submissionId - b.submissionId);

  for (const entry of ordered) {
    const tree = entry.tree || {};
    const entryProblems = tree.problems || [];
    const entryAnswers = tree.answers || [];

    // Joined by LABEL, never by index. The server guarantees the two arrays are
    // parallel and in the same order; that guarantee is what makes a mismatch
    // obvious, not something this function relies on.
    const answerByLabel = new Map();
    for (const answer of entryAnswers) {
      if (answer && answer.label != null) answerByLabel.set(answer.label, answer);
    }

    for (const problem of entryProblems) {
      if (!problem || problem.label == null) continue;

      const answer = answerByLabel.get(problem.label);

      // An answer exists -> answered. Otherwise pending, answer null.
      //
      // Covers all three cases with one rule: a pending entry carries no
      // answers so its labels go pending; a filled entry carries them so its
      // labels go answered. A NOT-pending entry missing an answer for a label
      // it mentioned is a malformed response — it stays pending and shows a
      // loading bar, which is truer than an answered card with nothing in it.
      if (!answer && entry.pending === false) {
        console.warn(
          `[derive] entry ${entry.tag} is not pending but has no answer for label ${problem.label}`
        );
      }

      problems[problem.label] = {
        status: answer ? 'answered' : 'pending',
        problem,                      // BY REFERENCE — see rule 1
        answer: answer || null,
      };
    }
  }

  // ── 2. OVERLAY THE DRAFTS ─────────────────────────────────────────────────
  //
  // A draft can only exist because `edit` was pressed on a card that was
  // already on screen, so every draft label should already be in `problems`.
  // One that is not means something upstream wrote a draft it should not have.
  // Ignore it — inventing a card from a draft alone would be worse — but say
  // so, because it is a bug rather than a state.
  for (const label of Object.keys(drafts || {})) {
    const existing = problems[label];

    if (!existing) {
      console.warn(`[derive] draft for label ${label}, which no log entry mentions — ignored`);
      continue;
    }

    problems[label] = {
      status: 'editing',
      problem: existing.problem,      // untouched, still what was SENT
      answer: null,                   // stale the moment editing begins
    };
  }

  // ── 3. THE COUNTER ────────────────────────────────────────────────────────
  //
  // The highest id in the log. Not held separately anywhere — the next request
  // is this plus one, so an empty log gives 0 and the first request is 1.
  let submissionId = 0;
  for (const entry of entries) {
    if (entry && entry.submissionId > submissionId) submissionId = entry.submissionId;
  }

  return { submissionId, problems };
}

// =============================================================================
// logReducer — the only thing that writes to the log
// =============================================================================
//
// APPEND-ONLY IN SPIRIT: `fill` replaces one entry's `answers` and clears its
// `pending`, and nothing else ever rewrites an entry. Entries are never removed
// except by `reset`, which is a genuinely new question wiping everything.
//
// Entries are appended in ARRIVAL order and may be out of order by
// submissionId. `derive` sorts at read time — see the note there.
//
// `fire` and `fill` are matched by TAG, not by index or submissionId, because
// the tag is what the caller holds across the await.

export const emptyLog = { entries: [] };

export function logReducer(log, action) {
  switch (action.type) {
    // A request has been SENT. The entry exists from this moment, pending,
    // holding the problems it sent and an empty answers array.
    case 'fire':
      return {
        entries: [
          ...log.entries,
          {
            tag: action.tag,
            submissionId: action.submissionId,
            pending: true,
            tree: { problems: action.problems, answers: [] },
          },
        ],
      };

    // The response landed. Fill that entry's answers and clear pending.
    // Everything else is left exactly as it was.
    case 'fill':
      return {
        entries: log.entries.map((e) =>
          e.tag === action.tag
            ? {
                ...e,
                pending: false,
                tree: {
                  problems: (action.tree && action.tree.problems) || e.tree.problems,
                  answers: (action.tree && action.tree.answers) || [],
                },
              }
            : e
        ),
      };

    // A genuinely new question. The generation counter blocks whatever is still
    // in flight from writing here.
    case 'reset':
      return emptyLog;

    default:
      return log;
  }
}

export default derive;
